import { useCallback, useState, useEffect } from 'react';

const STORAGE_KEY = 'lat_user_location';
const EVENT_NAME = 'lat-location-changed';

interface PersistedLocation {
  name: string;
  lat: number;
  lng: number;
}

function readLocation(): PersistedLocation | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.lat === 'number' && typeof parsed.lng === 'number' && parsed.name) {
      return parsed as PersistedLocation;
    }
    return null;
  } catch {
    return null;
  }
}

export function usePersistedLocation() {
  const [savedLocation, setSavedLocation] = useState<PersistedLocation | null>(readLocation);

  useEffect(() => {
    const handler = () => setSavedLocation(readLocation());
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []);

  const saveLocation = useCallback((name: string, lat: number, lng: number) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ name, lat, lng }));
    setSavedLocation({ name, lat, lng });
    window.dispatchEvent(new Event(EVENT_NAME));
  }, []);

  const clearLocation = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setSavedLocation(null);
    window.dispatchEvent(new Event(EVENT_NAME));
  }, []);

  return { savedLocation, saveLocation, clearLocation };
}
